import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import Assets from "./../../core/Assets";
import firebase from "./../firebase/Config";
import SuccessErrorDialog from "./SuccessErrorDialog";
import FullScreenUploadDialog from "./FullScreenUploadDialog";
import { LinearProgress, Box, Tooltip} from "@mui/material";
import {
  CheckCircle,
  Refresh,
  ExpandLess,
  ExpandMore,
  HighlightOff,
} from "@mui/icons-material";

interface FileProgress {
  fileKey: string;
  progress: number;
  status: "uploading" | "failed" | "completed" | "pending" | "out_of_size";
}

export interface FileStatus {
  name: string;
  uploadKey: string;
  status: string;
}

export interface ChildMethods {
  fetchFiles: () => FileStatus[];
}

export interface ChildProps {
  uploadStatusCallback(status: boolean): any;
}

const supportedFormats = [
  "jpeg",
  "jpg",
  "png",
  "gif",
  "bmp",
  "webp",
  "svg",
  "ico",
  "tiff",
  "heic",
  "heif",
  "mp4",
  "webm",
  "ogg",
  "mov",
  "avi",
  "mkv",
  "flv",
  "wmv",
];

const SeparateFileUploader = forwardRef<ChildMethods, ChildProps>(
  ({ uploadStatusCallback }, refKey) => {
    // Response Dialogs
    const [isProcessing, setProcessing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogInfo, setDialogInfo] = useState({
      title: "",
      message: "",
      type: "success" as "success" | "error" | "input",
    });

    // keys and references
    const stopUploadRef = useRef<{ [key: string]: boolean }>({});
    const fileKeysRef = useRef<{ [key: string]: string }>({});
    const selectedFilessRef = useRef<File[]>([]);

    const [isExpanded, setIsExpanded] = useState(true);
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{
      [key: string]: FileProgress;
    }>({});

    const [filePickerText, setFilePickerText] = useState("");
    const [uploadStatusText, setUploadStatusText] = useState("");

    useEffect(() => {
      setFilePickerText(
        files.length > 0 ? `${files.length} files` : "No file chosen"
      );

      if (files.length === 0) {
        setUploadStatusText("Uploading - 0/0");
        uploadStatusCallback(true);
      } else {
        const keys = Object.keys(uploadProgress);
        const completed = keys.filter(
          (x) =>
            uploadProgress[x].status === "completed" && fileKeysRef.current[x]
        );
        setUploadStatusText(
          `Uploading - ${completed.length}/${files.length ?? 0}`
        );
        uploadStatusCallback(completed.length === files.length);
      }
    }, [files, uploadProgress]);

    const getFileName = (file: File) => {
      const ms = parseInt(Date.now().toString());
      const newFileName = `${ms}-${file.name}`;
      return newFileName;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFiles = Array.from(event.target.files);
        event.target.value = "";

        if (files.length + selectedFiles.length > 15) {
          setDialogInfo({
            title: "Alert",
            message: "You can only upload a maximum of 15 files.",
            type: "error",
          });
          setDialogOpen(true);
          return;
        }

        const invalidFiles: string[] = [];

        Array.from(selectedFiles).forEach((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (fileExtension && supportedFormats.includes(fileExtension)) {
            // validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        });

        if (invalidFiles.length > 0) {
          setDialogInfo({
            title: "Alert",
            message: `Invalid files: ${invalidFiles.join(", ")}`,
            type: "error",
          });

          setDialogOpen(true);
          return;
        }

        selectedFilessRef.current = selectedFiles;

        // Check duplicate files.
        const duplicateFiles = selectedFilessRef.current.filter((f1) =>
          files.find((f2) => f1.name === f2.name)
        );
        if (duplicateFiles.length > 0) {
          setDialogInfo({
            title: "Confirm Replace",
            message: `<b>Duplicate files:- </b><br />
          ${duplicateFiles.map((x) => x.name).join("<br />")}<br /><br />
            <b>Do you want to replace it? </b>`,
            type: "input",
          });
          setDialogOpen(true);
          return;
        }

        addFilesToUpload("add");
      }
    };

    const addFilesToUpload = (type: "add" | "addNonDuplicate" | "replace") => {
      setProcessing(true);
      setTimeout(() => {
        if (type === "addNonDuplicate") {
          const duplicateFiles = selectedFilessRef.current.filter((f1) =>
            files.find((f2) => f1.name === f2.name)
          );
          let newFiles: File[] = [];
          for (const item of selectedFilessRef.current) {
            if (!duplicateFiles.find((x) => x.name === item.name)) {
              newFiles.push(item);
            }
          }
          selectedFilessRef.current = newFiles;
          type = "add";
        }

        if (type === "add") {
          setFiles([...files, ...selectedFilessRef.current]);
          for (const item of selectedFilessRef.current) {
            delete stopUploadRef.current[item.name];
            delete fileKeysRef.current[item.name];
            uploadFile(item);
          }
        }

        if (type === "replace") {
          const duplicateFiles = selectedFilessRef.current.filter((f1) =>
            files.find((f2) => f1.name === f2.name)
          );

          let oldProgress = uploadProgress;
          let oldFiles = files;


          // Remove duplicates files
          for (const item of duplicateFiles) {
            const fileUID = fileKeysRef.current[item.name];
            stopUploadRef.current[item.name] = true;
            delete fileKeysRef.current[item.name];

            delete oldProgress[item.name];
            oldFiles = oldFiles.filter((x) => x.name !== item.name);
            deleteStorageObject(`UserDataUpload/${fileUID}`);
          }

          oldFiles = [...oldFiles, ...selectedFilessRef.current];

          setFiles(oldFiles);
          for (const item of selectedFilessRef.current) {
            delete stopUploadRef.current[item.name];
            delete fileKeysRef.current[item.name];
            uploadFile(item);
          }
        }

        setProcessing(false);
      }, 2000);
    };

    const uploadFile = (file: File) => {
      // Check msx file size
      const fileSize = file.size / (1024 * 1024);
      if (fileSize > 400) {
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: {
            fileKey: fileKeysRef.current[file.name],
            progress: 0,
            status: "out_of_size",
          },
        }));
        return;
      }

      const fileUID = getFileName(file);
      fileKeysRef.current[file.name] = fileUID;

      const storageRef = ref(firebase.storage, `UserDataUpload/${fileUID}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      stopUploadRef.current[file.name] = false;

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          if (stopUploadRef.current[file.name]) {
            uploadTask.cancel();
            return;
          }

          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: {
              fileKey: fileKeysRef.current[file.name],
              progress,
              status: "uploading",
            },
          }));
        },
        () => {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: {
              fileKey: fileKeysRef.current[file.name],
              progress: 0,
              status: "failed",
            },
          }));
        },
        () => {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: {
              fileKey: fileKeysRef.current[file.name],
              progress: 100,
              status: "completed",
            },
          }));
        }
      );
    };

    const handleRetry = (fileName: string) => {
      const file = files.find((f) => f.name === fileName);
      if (file) uploadFile(file);
    };

    const getUploadPercent = (val: any) => {
      return Math.round(val ?? 0);
    };

    const deleteStorageObject = async (path: string): Promise<void> => {
      const storageRef = ref(firebase.storage, path);

      try {
        await deleteObject(storageRef);
      } catch (error: any) {
        // console.log(`Failed to delete file at path "${path}":`, error.message);
      }
    };

    const onDelete = (file: File) => {
      const fileUID = fileKeysRef.current[file.name];
      stopUploadRef.current[file.name] = true;
      delete fileKeysRef.current[file.name];
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
      setFiles(files.filter((f) => f.name !== file.name));
      deleteStorageObject(`UserDataUpload/${fileUID}`);
    };

    useImperativeHandle(refKey, () => ({
      fetchFiles: () => {
        const records = [];
        const keys = Object.keys(uploadProgress);
        for (const key of keys) {
          if (fileKeysRef.current[key]) {
            records.push({
              name: key,
              uploadKey: fileKeysRef.current[key],
              status: uploadProgress[key].status,
            });
          }
        }
        return records;
      },
    }));

    const getFileSizeString = (file: File) => {
      const size = (file.size / (1024 * 1024)).toFixed(2);
      if (parseInt(size) === 0) {
        return `Size: ${(file.size / 1024).toFixed(2)} KB`;
      }
      return `Size: ${size} MB`;
    };

    return (
      <div className="p-0">
        <label htmlFor="fileUpload" className="form-label">
          <b>Stove Images and Videos</b>
        </label>
        <div
          className="d-flex align-items-center mb-3"
          onClick={() => {
            document.getElementById("files")?.click();
          }}
        >
          <div
            className="btn btn-light border d-flex align-items-center px-2"
            style={{
              cursor: "pointer",
              borderBottomRightRadius: "0",
              borderTopRightRadius: "0",
            }}
          >
            Choose Files
          </div>

          <div
            className="flex-grow-1 bg-white border align-items-center rounded custom-file-picker-text d-flex"
            style={{
              cursor: "pointer",
            }}
          >
            <span className="ms-3">{filePickerText}</span>
          </div>

          <input
            style={{ display: "none" }}
            type="file"
            id="files"
            multiple
            accept=".jpeg,.jpg,.png,.gif,.bmp,.webp,.svg,.ico,.tiff,.heic,.heif,.mp4,.webm,.ogg,.mov,.avi,.mkv,.flv,.wmv"
            capture={false}
            onChange={handleFileChange}
          />
        </div>

        <p>
          Accepted File Formats: JPEG, JPG, PNG, GIF, BMP, WEBP, SVG, ICO, TIFF,
          HEIC, HEIF, MP4, WEBM, OGG, MOV, AVI, MKV, FLV, WMV.
        </p>
        <p>Maximum File Size: 400 MB.</p>

        <div
          className="d-flex flex-row justify-content-between border border-gray-500 rounded-md p-2 cursor-pointer"
          style={{ width: "170px", cursor: "pointer" }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div style={{ cursor: "pointer" }}>
            <span
              className="text-gray-700 mr-2 cursor-pointer"
              style={{ fontSize: 15, fontWeight: "bold", cursor: "pointer" }}
            >
              {uploadStatusText}
            </span>
          </div>
          <div
            className="text-gray-700 mr-2 cursor-pointer"
            style={{ fontSize: 15, fontWeight: "bold", cursor: "pointer" }}
          >
            {isExpanded ? (
              <ExpandLess className="text-gray-500" fontSize="small" />
            ) : (
              <ExpandMore className="text-gray-500" fontSize="small" />
            )}
          </div>
        </div>

        <div className="mt-4">
          {isExpanded &&
            files.map((file, index) => (
              <>
                <div key={index}>
                  {uploadProgress[file.name]?.status === "out_of_size" ? (
                    <div className={"mb-2 p-2 rounded file-failed-box"}>
                      <div
                        className="d-flex flex-row justify-content-center"
                        style={{ height: "80px" }}
                      >
                        <div className="p-2" style={{ alignItems: "center" }}>
                          <img
                            src={Assets.fileFailedIcon}
                            height={42}
                            width={42}
                            alt="icon"
                          />
                        </div>
                        <div className="p-2 flex-grow-1 text-truncate">
                          <span
                            style={{
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            File Name: {file.name}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            {getFileSizeString(file)}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "12px",
                              color: "red",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            Maximum 400 MB file size allowed.
                          </span>
                        </div>
                        <div
                          className="p-1"
                          style={{
                            width: 72,
                            minWidth: 72,
                            alignContent: "center",
                            textAlign: "end",
                          }}
                        >
                          <Tooltip
                            title={"Remove"}
                            style={{ cursor: "pointer" }}
                          >
                            <HighlightOff
                              sx={{ fontSize: 22, color: "red" }}
                              onClick={() => onDelete(file)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ) : uploadProgress[file.name]?.status === "failed" ? (
                    <div className={"mb-2 p-2 rounded file-failed-box"}>
                      <div
                        className="d-flex flex-row justify-content-center"
                        style={{ height: "80px" }}
                      >
                        <div className="p-2" style={{ alignItems: "center" }}>
                          <img
                            src={Assets.fileFailedIcon}
                            height={42}
                            width={42}
                            alt="icon"
                          />
                        </div>
                        <div className="p-2 flex-grow-1 text-truncate">
                          <span
                            style={{
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            File Name: {file.name}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            {getFileSizeString(file)}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "12px",
                              color: "red",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            Something went wrong, please try again.
                          </span>
                        </div>
                        <div
                          className="p-1"
                          style={{
                            width: 72,
                            minWidth: 72,
                            alignContent: "center",
                            textAlign: "end",
                          }}
                        >
                          <Tooltip
                            title={"Retry"}
                            style={{ cursor: "pointer" }}
                          >
                            <Refresh
                              className="me-2"
                              sx={{ fontSize: 22, color: "red" }}
                              onClick={() => handleRetry(file.name)}
                            />
                          </Tooltip>
                          <Tooltip
                            title={"Remove"}
                            style={{ cursor: "pointer" }}
                          >
                            <HighlightOff
                              sx={{ fontSize: 22, color: "red" }}
                              onClick={() => onDelete(file)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ) : uploadProgress[file.name]?.status === "completed" ? (
                    <div className={"mb-2 p-2 rounded file-success-box"}>
                      <div
                        className="d-flex flex-row justify-content-center"
                        style={{ height: "60px" }}
                      >
                        <div className="p-2">
                          <img
                            src={Assets.fileSuccessIcon}
                            height={42}
                            width={42}
                            alt="icon"
                          />
                        </div>
                        <div className="p-2 flex-grow-1 text-truncate">
                          <span
                            style={{
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            File Name: {file.name}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            {getFileSizeString(file)}
                          </span>
                        </div>
                        <div
                          className="p-1"
                          style={{
                            width: 72,
                            minWidth: 72,
                            alignContent: "center",
                            textAlign: "end",
                          }}
                        >
                          <Tooltip title={"Completed"}>
                            <CheckCircle
                              className="me-2"
                              sx={{ fontSize: 22, color: "green" }}
                            />
                          </Tooltip>
                          <Tooltip
                            title={"Remove"}
                            style={{ cursor: "pointer" }}
                          >
                            <HighlightOff
                              sx={{ fontSize: 22, color: "red" }}
                              onClick={() => onDelete(file)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={"mb-2 p-2 border rounded border-gray-300"}>
                      <div
                        className="d-flex flex-row justify-content-center"
                        style={{ height: "56px" }}
                      >
                        <div className="p-2" style={{ alignItems: "center" }}>
                          <img
                            src={Assets.fileIcon}
                            height={42}
                            width={42}
                            alt="icon"
                          />
                        </div>
                        <div className="p-2 flex-grow-1 text-truncate">
                          <span
                            style={{
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            File Name: {file.name}
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            {getFileSizeString(file)}
                          </span>
                        </div>
                        <div
                          className="p-1"
                          style={{
                            width: 72,
                            alignContent: "center",
                            textAlign: "end",
                          }}
                        >
                          <Tooltip
                            title={"Remove"}
                            style={{ cursor: "pointer" }}
                          >
                            <HighlightOff
                              sx={{ fontSize: 22, color: "red" }}
                              onClick={() => onDelete(file)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                      <div
                        className="d-flex flex-row"
                        style={{ height: "18px" }}
                      >
                        <div
                          className="px-2 flex-grow-1"
                          style={{
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ width: "100%", marginTop: "12px" }}>
                            <LinearProgress
                              variant="determinate"
                              value={uploadProgress[file.name]?.progress || 0}
                              sx={{
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor: "#E8772C", // Custom color for the progress bar
                                },
                                backgroundColor: "#f2f2f2", // Light gray for the track background
                              }}
                            />
                          </Box>
                        </div>
                        <div
                          className="ps-1"
                          style={{
                            textAlign: "end",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{ fontSize: "12px", fontWeight: "bold" }}
                          >
                            {getUploadPercent(
                              uploadProgress[file.name]?.progress
                            )}{" "}
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ))}
        </div>
        <SuccessErrorDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={dialogInfo.title}
          message={dialogInfo.message}
          type={dialogInfo.type}
          noButton={{
            label: "NO",
            onClick: () => {
              setDialogOpen(false);
              addFilesToUpload("addNonDuplicate");
            },
          }}
          yesButton={{
            label: "YES",
            onClick: () => {
              setDialogOpen(false);
              addFilesToUpload("replace");
            },
          }}
        />
        <FullScreenUploadDialog
          titleText="Importing Files..."
          msgText="Please wait while the files are importing."
          open={isProcessing}
          onClose={() => {}}
        />
      </div>
    );
  }
);

export default SeparateFileUploader;
